import React from 'react';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  LinearProgress,
  Fade,
  Card,
  CardContent
} from '@mui/material';
import { LoadingSkeleton } from './LoadingSkeleton';

interface LoadingStateProps {
  loading: boolean;
  error?: Error | string | null;
  children: React.ReactNode;
  skeleton?: 'dashboard' | 'table' | 'card' | 'list';
  skeletonCount?: number;
  loadingText?: string;
  errorMessage?: string;
  showLinearProgress?: boolean;
  minHeight?: number | string;
  onRetry?: () => void;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  loading,
  error,
  children,
  skeleton,
  skeletonCount = 3,
  loadingText,
  errorMessage,
  showLinearProgress = false,
  minHeight,
  onRetry
}) => {
  // Show error state
  if (error) {
    const errorMsg = typeof error === 'string' ? error : error.message;
    const displayMessage = errorMessage || errorMsg || 'Something went wrong';
    
    return (
      <Box 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          minHeight: minHeight || 'auto'
        }}
      >
        <Card>
          <CardContent>
            <Typography color="error" variant="h6" gutterBottom>
              Oops! Something went wrong
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {displayMessage}
            </Typography>
            {onRetry && (
              <button 
                onClick={onRetry}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Show loading state
  if (loading) {
    // Use skeleton if specified
    if (skeleton) {
      return (
        <Fade in={loading} timeout={300}>
          <Box sx={{ minHeight: minHeight || 'auto' }}>
            <LoadingSkeleton variant={skeleton} count={skeletonCount} />
          </Box>
        </Fade>
      );
    }

    // Default loading spinner
    return (
      <Fade in={loading} timeout={300}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            p: 4,
            minHeight: minHeight || 200
          }}
        >
          {showLinearProgress ? (
            <Box sx={{ width: '100%', maxWidth: 400 }}>
              <LinearProgress />
              {loadingText && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mt: 2, textAlign: 'center' }}
                >
                  {loadingText}
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <CircularProgress size={40} />
              {loadingText && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mt: 2 }}
                >
                  {loadingText}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Fade>
    );
  }

  // Show content
  return <>{children}</>;
};

// Specialized loading states for common use cases
export const DashboardLoading: React.FC<{ loading: boolean; children: React.ReactNode }> = ({
  loading,
  children
}) => (
  <LoadingState
    loading={loading}
    skeleton="dashboard"
    loadingText="Loading your dashboard..."
  >
    {children}
  </LoadingState>
);

export const TableLoading: React.FC<{ 
  loading: boolean; 
  children: React.ReactNode;
  rowCount?: number;
}> = ({
  loading,
  children,
  rowCount = 5
}) => (
  <LoadingState
    loading={loading}
    skeleton="table"
    skeletonCount={rowCount}
    loadingText="Loading data..."
  >
    {children}
  </LoadingState>
);

export const CardLoading: React.FC<{ loading: boolean; children: React.ReactNode }> = ({
  loading,
  children
}) => (
  <LoadingState
    loading={loading}
    skeleton="card"
    loadingText="Loading..."
  >
    {children}
  </LoadingState>
);

// Hook for managing loading states
export const useLoadingState = (initialLoading = false) => {
  const [loading, setLoading] = React.useState(initialLoading);
  const [error, setError] = React.useState<Error | string | null>(null);

  const startLoading = React.useCallback(() => {
    setLoading(true);
    setError(null);
  }, []);

  const stopLoading = React.useCallback(() => {
    setLoading(false);
  }, []);

  const setErrorState = React.useCallback((error: Error | string) => {
    setLoading(false);
    setError(error);
  }, []);

  const reset = React.useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return {
    loading,
    error,
    startLoading,
    stopLoading,
    setError: setErrorState,
    reset
  };
};

export default LoadingState;
